import { Injectable } from '@angular/core';
import _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { ProjectStreamService } from '../../common/current-project/project-stream.service';
import { FileUtils } from '../../common/utility/file-utils';
import { Style } from './style';
import {
    StyleSymbol,
    SymbologyOverride,
    SymbologyOverrideChoice,
    SymbolOverrideDTO,
    SymbolShapeDefinition
} from './style-symbology';
import { StyleSymbologyService } from './style-symbology.service';

@Injectable({
    providedIn: 'root'
})
export class StyleSymbologyStreamService {
    // UUID for the default point marker symbol - Dot.
    public readonly DEFAULT_SYMBOL_UUID = '623eaaa5-14fa-4653-b464-ebb4a2e56a47';

    public symbolDefinitionsStream = new BehaviorSubject<SymbolShapeDefinition[]>([]);

    constructor(
        private styleSymbologyService: StyleSymbologyService,
        private projectStreamService: ProjectStreamService
    ) {}

    public getSymbolDefinitions(sortBy: keyof SymbolShapeDefinition = 'symbolName'): void {
        this.styleSymbologyService.getAvailableSymbolDefinitions().then(definitions => {
            definitions.forEach(definition => {
                if (definition.svgImage) {
                    FileUtils.createSymbolCssClass(definition.id, definition.svgImage);
                    definition.imageClass = `icon-${definition.id}-symbol-definition`;
                }
            });

            definitions.sort((a, b) => {
                const aValue = a[sortBy];
                const bValue = b[sortBy];
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            });

            this.symbolDefinitionsStream.next(definitions);
        });
    }

    public async createStyleSymbol(symbol: StyleSymbol): Promise<StyleSymbol> {
        const projectId = this.projectStreamService.currentProjectStream.getValue().id;

        return await this.styleSymbologyService.createSymbol(projectId, symbol);
    }

    public async updateStyleSymbol(symbol: StyleSymbol): Promise<StyleSymbol> {
        const projectId = this.projectStreamService.currentProjectStream.getValue().id;

        return await this.styleSymbologyService.updateSymbol(projectId, symbol);
    }

    public async deleteStyleSymbol(symbol: StyleSymbol): Promise<boolean> {
        const projectId = this.projectStreamService.currentProjectStream.getValue().id;

        return await this.styleSymbologyService.deleteSymbol(projectId, symbol);
    }

    public async createOrUpdateSymbologyOverrides(
        styleId: string,
        previousStyleOverrides: SymbologyOverrideChoice[],
        overrides: SymbologyOverrideChoice[]
    ): Promise<SymbologyOverrideChoice[]> {
        let response: SymbologyOverrideChoice[] = previousStyleOverrides ?? [];
        const projectId = this.projectStreamService.currentProjectStream.getValue().id;

        if (overrides?.length && !previousStyleOverrides?.length) {
            let newOverrides = await this.addNewOverrides(projectId, styleId, overrides);
            newOverrides.forEach(override => {
                let newOverride = this.symbologyOverrideChoiceFromOverride(SymbologyOverride.fromDTO(override));
                response.push(newOverride);
            });
        } else {
            if (previousStyleOverrides) {
                // Add, remove or edit existing overrides
                let existingOverrides = previousStyleOverrides ?? [];

                const overridesToAdd = overrides.filter(
                    override =>
                        !existingOverrides.some(
                            existing =>
                                existing.choiceField === override.choiceField &&
                                existing.choiceValue === override.choiceValue
                        )
                );
                const overridesToRemove = existingOverrides.filter(
                    existing =>
                        !overrides.some(
                            override =>
                                override.choiceField === existing.choiceField &&
                                override.choiceValue === existing.choiceValue
                        )
                );

                const existingOverridesToEdit = existingOverrides.filter(existing =>
                    overrides.some(
                        override =>
                            (existing.id === override.id || 
                             (existing.choiceField === override.choiceField && existing.choiceValue === override.choiceValue)) &&
                            !_.isEqual(existing, override)
                    )
                );

                if (overridesToRemove.length) {
                    await this.removeOldOverrides(projectId, overridesToRemove);
                    overridesToRemove.forEach(override => {
                        response = response.filter(o => o.id !== override.id);
                    });
                    existingOverrides = existingOverrides.filter(
                        existing => !overridesToRemove.some(override => override.id === existing.id)
                    );
                }

                if (existingOverridesToEdit.length) {
                    let edittedOverride = await this.editExistingOverrides(
                        projectId,
                        styleId,
                        overrides,
                        existingOverrides
                    );
                    response = edittedOverride.map(override =>
                        this.symbologyOverrideChoiceFromOverride(SymbologyOverride.fromDTO(override))
                    );
                }
                if (overridesToAdd.length) {
                    let newOverrides = await this.addNewOverrides(projectId, styleId, overridesToAdd);
                    newOverrides.forEach(override => {
                        let newOverride = this.symbologyOverrideChoiceFromOverride(SymbologyOverride.fromDTO(override));
                        response.push(newOverride);
                    });
                }
            }
        }

        return response;
    }

    private async addNewOverrides(
        projectId: string,
        styleId: string,
        overrides: SymbologyOverrideChoice[]
    ): Promise<SymbolOverrideDTO[]> {
        const newStyleOverrides = overrides.map(override => {
            const filter = `$${override.choiceField} EQ '${override.choiceValue}'`;

            return {
                filter: filter,
                styleOverride: {
                    lineColor: override.lineColor,
                    fillColor: override.fillColor
                },
                symbolOverride: {
                    fillColor: override.fillColor,
                    outlineColor: override.outlineColor,
                    color: override.color
                }
            } as SymbologyOverride;
        });

        return await this.styleSymbologyService.createStyleOverrides(projectId, styleId, newStyleOverrides);
    }

    public async removeOldOverrides(
        projectId: string,
        overrides: SymbologyOverrideChoice[]
    ): Promise<SymbolOverrideDTO[]> {
        return Promise.all(
            overrides
                .filter(override => override.id)
                .map(override => this.styleSymbologyService.removeStyleOverride(projectId, override.id))
        );
    }

    private async editExistingOverrides(
        projectId: string,
        styleId: string,
        overrides: SymbologyOverrideChoice[],
        existingStyleOverrides: SymbologyOverrideChoice[]
    ): Promise<SymbolOverrideDTO[]> {
        const updatedExistingOverrides: SymbologyOverride[] = existingStyleOverrides.map(existingOverride => {
            const matchingOverride = overrides.find(
                o => o.choiceField === existingOverride.choiceField && o.choiceValue === existingOverride.choiceValue
            );

            const filter = `$${existingOverride.choiceField} EQ '${existingOverride.choiceValue}'`;

            if (
                matchingOverride &&
                (existingOverride.lineColor !== matchingOverride.lineColor ||
                    existingOverride.fillColor !== matchingOverride.fillColor ||
                    existingOverride.outlineColor !== matchingOverride.outlineColor ||
                    existingOverride.color !== matchingOverride.color)
            ) {
                // Edited override.
                return {
                    styleId: styleId,
                    filter: filter,
                    styleOverride: {
                        lineColor: matchingOverride.lineColor,
                        fillColor: matchingOverride.fillColor
                    },
                    symbolOverride: {
                        fillColor: matchingOverride.fillColor,
                        outlineColor: matchingOverride.outlineColor,
                        color: matchingOverride.color
                    }
                } as SymbologyOverride;
            } else {
                // Unedited override.
                return {
                    styleId: styleId,
                    filter: filter,
                    styleOverride: {
                        lineColor: existingOverride.lineColor,
                        fillColor: existingOverride.fillColor
                    },
                    symbolOverride: {
                        fillColor: existingOverride.fillColor,
                        outlineColor: existingOverride.outlineColor,
                        color: existingOverride.color
                    }
                } as SymbologyOverride;
            }
        });

        return await this.styleSymbologyService.updateStyleOverrides(projectId, styleId, updatedExistingOverrides);
    }

    public symbologyOverrideChoiceFromOverride(override: SymbologyOverride): SymbologyOverrideChoice {
        const parsed = Style.parseFilter(override.filter);
        return {
            id: override.id,
            choiceField: parsed.choiceField,
            choiceValue: parsed.choiceValue,
            lineColor: override.styleOverride.lineColor,
            fillColor: override.styleOverride.fillColor,
            outlineColor: override.symbolOverride.outlineColor,
            color: override.symbolOverride.color
        };
    }
}
